import jfetch from "../utils/jfetch";
import { snakeCaseKeys } from "../utils";
import * as yup from "yup";
import { extractRelationships, normalizeRecord } from "./api_utils";
import { startCase } from "lodash";

/**
 * Normalizes a user record
 * @param user
 */
export function normalizeUser(user: IUserRecord): IUser {
  return normalizeRecord(user);
}

/*
  NEW functions for normalized data
 */

/**
 * Tells if a user is the owner of an org.
 * @param user
 * @param org
 */
export function isOrgOwner(user: IUser, org: IOrganization) {
  return user.id === org.owner.id.toString();
}

/**
 * Tells if a user is in a particular org
 * @param user
 * @param org
 */
export function inOrg(user: IUser, org: IOrganization) {
  return user.organizationId.toString() === org.id;
}

/**
 * Returns a normalized user
 * @param userId
 */
export function getUser(userId: string) {
  return getUserRecord(userId).then(resp => normalizeUser(resp.data));
}

/**
 * Gets a list of normalized users from the api
 * @param userIds
 */
export function getUsers(userIds: string[]) {
  return Promise.all(userIds.map(id => getUser(id)));
}

/**
 * Gets the managed orgs of a user for users with some level of admin access.
 * @param userId
 */
export function getManagedOrgs(userId: string) {
  return jfetch
    .get(`/employees/${userId}/managed_orgs`)
    .then(resp => resp.data.map(org => normalizeRecord(org)));
}

// ===============================================

/*
 Data fetching functions
 */

/**
 * Gets a user record from the API
 * @param id
 */
export function getUserRecord(id: string | number) {
  return jfetch.get<IJsonApiResponse<IUserRecord>>(`/employees/${id}`);
}

/**
 * Sends an invite for a user
 * orgId is only required by the route for admins, but we require it for all calls to be safe
 * @param attrs
 * @param orgId
 */
export function inviteUser(
  attrs: Partial<IUserAttributes>,
  orgId: string | number
) {
  return jfetch.post(`/employees`, { user: snakeCaseKeys(attrs), org: orgId });
}

/**
 * Saves a user record
 * @deprecated
 * @param record
 */
export function saveUserRecord(record: IUserRecord) {
  return jfetch.put(`/employees/${record.id}`, {
    user: snakeCaseKeys(record.attributes),
  });
}

/**
 * Patch a user by providing a partial attributes object
 * @param userId
 * @param userAttributes
 */
export function patchUser(userId: string | number, userAttributes: Dictionary) {
  return jfetch.patch(`/employees/${userId}`, {
    user: snakeCaseKeys(userAttributes),
  });
}

/**
 * Applies a role to a user
 * @param user
 * @param role
 */
export function addRoleToUser(user, role) {
  return patchUser(user.id, { roles: [role, ...user.roles] });
}

export function getAvailableManagersForUser(userId: string | number) {
  return jfetch.get<IJsonApiResponse<IUserRecord[]>>(
    `/employees/${userId}/available_managers`
  );
}

export const userValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email()
    .required(),
  jobTitle: yup.string(),
  managerId: yup.number().nullable(true),
  name: yup.string().required(),
  phoneNumber: yup.string(),
  hireDate: yup.string().nullable(true),
});

export function getUserSubordinates(userId: number | string) {
  return jfetch
    .get<IJsonApiResponse<IUserRecord[]>>(`/employees/${userId}/subordinates`)
    .then(resp => resp.data.map(normalizeUser));
}

export function disableUser(userId: number | string) {
  return jfetch.get(`/employees/${userId}/disable_user`);
}

export const formatRole = (roles: string[]) => {
  const ROLE_LABEL_OVERRIDES = {
    admin: "Super Admin",
    company_admin: "Chartec Admin",
  };
  return roles.map(r => ROLE_LABEL_OVERRIDES[r] || startCase(r));
};

export function getSession() {
  return jfetch.get(`/api/core/session`);
}

export function updateSession(sessionParams: { viewed_beta_popup?: boolean }) {
  return jfetch.post(`/api/core/session`, sessionParams);
}

export const getAllUsers = (options: any) => {
  return jfetch.get("/employees", options);
};



// WEBPACK FOOTER //
// ./app/javascript/libs/user.ts